var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Auth } from '@freelancer/auth';
import { TimeUtils } from '@freelancer/time-utils';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import * as Rx from 'rxjs';
import { delay, mapTo, switchMap, take, tap } from 'rxjs/operators';
import { FacebookPixelTracking } from './facebook-pixel-tracking.service';
import { GoogleTracking } from './google-tracking.service';
var Tracking = /** @class */ (function () {
    function Tracking(http, auth, cookies, timeUtils, googleTracking, facebookPixelTracking, config, platformId, locale) {
        var _this = this;
        this.http = http;
        this.auth = auth;
        this.cookies = cookies;
        this.timeUtils = timeUtils;
        this.googleTracking = googleTracking;
        this.facebookPixelTracking = facebookPixelTracking;
        this.config = config;
        this.platformId = platformId;
        this.locale = locale;
        this.sequenceNum = 0;
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        var sessionId = this.cookies.get(this.config.sessionCookie);
        if (!sessionId) {
            // Generate a new one if not.
            sessionId = this.generateSessionId();
            this.cookies.put('_tracking_session', sessionId, {
                expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
            });
        }
        this.sessionId = sessionId;
        // set the external referrer on start
        this.referrerUrl = document.referrer;
        // send the event backlog if any
        try {
            var backlog = localStorage.getItem('trackingBacklog');
            if (backlog) {
                var itemsMap = JSON.parse(backlog);
                Object.values(itemsMap).forEach(function (item) {
                    _this.sendBeacon(item);
                });
            }
            localStorage.setItem('trackingBacklog', JSON.stringify({}));
        }
        catch (e) {
            // ignore the errors, e.g. quota is full or security error
            console.error(e);
        }
    }
    // DO NOT CALL THAT FROM THE app/ CODE. IT'LL BE RESTRICTED SOON.
    Tracking.prototype.track = function (eventType, eventData) {
        var _this = this;
        if (eventData === void 0) { eventData = {}; }
        return this.auth.authState$
            .pipe(take(1))
            .toPromise()
            .then(function (auth) {
            var e = __assign({
                acct: window.location.hostname.replace(/^www/, ''),
                t: Date.now().toString(),
                build_timestamp: _this.config.buildTimestamp,
                en: eventType,
                location: window.location.href,
                user_id: auth ? parseInt(auth.userId, 10) : undefined,
                session_id: _this.sessionId,
            }, eventData);
            return _this.sendBeacon(e);
        });
    };
    Tracking.prototype.getSessionId = function () {
        // sessionId could eventually be turned into a true observable
        return Rx.of(this.sessionId);
    };
    Tracking.prototype.trackPageView = function (is404) {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        this.track('page_view', {
            language: this.locale,
            referrer_url: this.referrerUrl,
            screenHeight: window.screen.height,
            screenWidth: window.screen.width,
            windowInnerHeight: window.innerHeight,
            windowInnerWidth: window.innerWidth,
            is_404: is404,
        });
        // update the referrerUrl so that the next page_view event contains it.
        this.referrerUrl = window.location.href;
        // if GA tracking is enabled, track the page view there as well
        if (this.config.gaTrackingId) {
            this.googleTracking.trackPageView();
        }
        // if the FB pixel is enabled, track the page view there as well
        if (this.config.facebookPixelId) {
            this.facebookPixelTracking.trackPageView();
        }
    };
    Tracking.prototype.trackCustomEvent = function (name, extraData) {
        this.track('custom_event', {
            name: name,
            extra_data: extraData,
        });
    };
    Tracking.prototype.trackHeartbeat = function (name, referenceType, referenceId) {
        var _this = this;
        // don't setup heartbeat tracking on the server-side
        if (!isPlatformBrowser(this.platformId)) {
            return function () {
                // do nothing
            };
        }
        var startTime = Date.now();
        var count = 0;
        var interval = 1000;
        var timeoutId;
        var track = function () {
            count += 1;
            _this.track('heart_beat', {
                name: name,
                start_time: startTime,
                count: count,
                reference: referenceType,
                reference_id: referenceId,
            });
            interval = Math.round(interval * 1.15);
            timeoutId = _this.timeUtils.setTimeout(function () { return track(); }, interval);
        };
        track();
        return function () {
            clearTimeout(timeoutId);
        };
    };
    Tracking.prototype.generateSessionId = function () {
        var s4 = function () {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        };
        return "" + s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4();
    };
    Tracking.prototype.sendBeacon = function (payload) {
        var _this = this;
        var beaconId = this.sequenceNum++;
        // save the event for later in case of a hard navigation
        try {
            var backlog = JSON.parse(localStorage.getItem('trackingBacklog') || '{}');
            backlog[beaconId] = payload;
            localStorage.setItem('trackingBacklog', JSON.stringify(backlog));
        }
        catch (e) {
            // ignore the errors, e.g. quota is full or security error
            console.error(e);
        }
        // if GA tracking is enabled, track the click event there
        if (this.config.gaTrackingId && payload.action === 'click') {
            this.googleTracking.trackClickEvent(payload);
        }
        return Rx.of(payload)
            .pipe(
        // this prevents cancelling in-flight requests in case a user_action
        // event triggers a hard navigation
        delay(100), switchMap(function (e) { return _this.http.post(_this.config.trackingEndpoint, e); }), tap(function () {
            // Remove the event from the backlog once it has been sent
            try {
                var backlog2 = JSON.parse(localStorage.getItem('trackingBacklog') || '{}');
                delete backlog2[beaconId];
                localStorage.setItem('trackingBacklog', JSON.stringify(backlog2));
            }
            catch (e) {
                // ignore the errors, e.g. quota is full or security error
                console.error(e);
            }
        }), mapTo(undefined))
            .toPromise()
            .catch(function (err) {
            if (err.error instanceof Error) {
                // A client-side or network error occurred.
                throw err.error;
            }
            else if (err.status === 0) {
                // HTTP status 0 means that the request was blocked by the client
                // (browser), which is often due to an Ad blocker.
                console.error(err.message);
            }
            else {
                // The backend returned an unsuccessful response code
                throw err.message;
            }
        });
    };
    return Tracking;
}());
export { Tracking };
